import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  LocalShipping,
  ShoppingBag,
  Payment,
  Receipt,
  CheckCircle,
  Error,
  Store,
  LocationOn,
  Phone,
  Email,
  AccessTime,
} from '@mui/icons-material';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';

const PaymentForm = ({ 
  orderType, // 'delivery' or 'takeaway'
  selectedOutlet,
  cart,
  onClose,
  onSuccess 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Customer Details
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    
    // Delivery Details (if delivery)
    deliveryAddress: '',
    deliveryInstructions: '',
    
    // Takeaway Details (if takeaway)
    pickupTime: '',
    pickupInstructions: '',
    
    // Payment Details
    paymentMethod: 'hitpay',
    specialInstructions: '',
  });

  const [errors, setErrors] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const { getCartTotal, clearCart } = useRestaurant();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();

  const cartTotal = getCartTotal ? getCartTotal() : 0;
  const deliveryFee = orderType === 'delivery' ? (cartTotal > 500 ? 0 : 50) : 0;
  const taxes = Math.round(cartTotal * 0.18);
  const finalTotal = cartTotal + deliveryFee + taxes;

  // Generate pickup time slots for next 24 hours
  const getPickupTimeSlots = () => {
    const slots = [];
    const now = new Date();
    const startHour = now.getHours() + 1; // Start from next hour
    
    for (let hour = startHour; hour < 24; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    
    // Next day slots
    for (let hour = 0; hour < 22; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    
    return slots;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.customerName.trim()) newErrors.customerName = 'Name is required';
    if (!formData.customerPhone.trim()) newErrors.customerPhone = 'Phone is required';
    if (!formData.customerEmail.trim()) newErrors.customerEmail = 'Email is required';
    
    // Delivery specific validation
    if (orderType === 'delivery') {
      if (!formData.deliveryAddress.trim()) newErrors.deliveryAddress = 'Delivery address is required';
    }
    
    // Takeaway specific validation
    if (orderType === 'takeaway') {
      if (!formData.pickupTime) newErrors.pickupTime = 'Pickup time is required';
    }
    
    // Phone validation
    if (formData.customerPhone && !/^[0-9+\-\s()]{10,}$/.test(formData.customerPhone)) {
      newErrors.customerPhone = 'Please enter a valid phone number';
    }
    
    // Email validation
    if (formData.customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerEmail)) {
      newErrors.customerEmail = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Create order data
      const orderData = {
        outlet_id: selectedOutlet.id,
        order_type: orderType,
        customer_details: {
          name: formData.customerName,
          phone: formData.customerPhone,
          email: formData.customerEmail,
        },
        items: cart.map(item => ({
          item_id: item.itemId || item.id,
          quantity: item.quantity,
          selected_size: item.selectedSize,
          selected_addons: item.selectedAddons,
          special_instructions: item.specialInstructions,
          price: item.totalPrice || item.price,
        })),
        delivery_details: orderType === 'delivery' ? {
          address: formData.deliveryAddress,
          instructions: formData.deliveryInstructions,
        } : null,
        takeaway_details: orderType === 'takeaway' ? {
          pickup_time: formData.pickupTime,
          instructions: formData.pickupInstructions,
        } : null,
        special_instructions: formData.specialInstructions,
        subtotal: cartTotal,
        delivery_fee: deliveryFee,
        taxes: taxes,
        total: finalTotal,
        payment_method: formData.paymentMethod,
      };

      // Here you would integrate with your payment gateway (HitPay)
      // For now, we'll simulate a successful payment
      console.log('Order Data:', orderData);
      
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Clear cart and show success
      if (clearCart) clearCart();
      showSuccess('Order placed successfully! Redirecting to payment...');
      
      // Redirect to payment gateway or show success
      setTimeout(() => {
        onSuccess && onSuccess(orderData);
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error('Payment error:', error);
      showError('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      label: 'Customer Details',
      description: 'Enter your contact information',
      icon: <Receipt />,
    },
    {
      label: orderType === 'delivery' ? 'Delivery Details' : 'Pickup Details',
      description: orderType === 'delivery' ? 'Enter delivery address' : 'Select pickup time',
      icon: orderType === 'delivery' ? <LocalShipping /> : <ShoppingBag />,
    },
    {
      label: 'Order Summary',
      description: 'Review your order and proceed to payment',
      icon: <Payment />,
    },
  ];

  if (!selectedOutlet || !cart || cart.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Error color="error" sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          Invalid Order
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please select an outlet and add items to cart first.
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" gutterBottom>
          {orderType === 'delivery' ? '🚚 Delivery Order' : '🛍️ Takeaway Order'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Complete your order from {selectedOutlet.name}
        </Typography>
      </Box>

      {/* Outlet Info Card */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Store color="primary" />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight="600">
              {selectedOutlet.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedOutlet.address}
            </Typography>
          </Box>
          <Chip 
            label={orderType === 'delivery' ? 'Delivery' : 'Takeaway'} 
            color="primary" 
            variant="outlined"
          />
        </Box>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {selectedOutlet.is_delivery_available && (
            <Chip 
              label="🚚 Delivery Available" 
              size="small" 
              color="success" 
              variant="outlined"
            />
          )}
          {selectedOutlet.is_takeaway_available && (
            <Chip 
              label="🛍️ Takeaway Available" 
              size="small" 
              color="warning" 
              variant="outlined"
            />
          )}
        </Box>
      </Paper>

      {/* Stepper */}
      <Stepper activeStep={activeStep} orientation={isMobile ? 'vertical' : 'horizontal'} sx={{ mb: 4 }}>
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel 
              icon={step.icon}
              sx={{ 
                '& .MuiStepLabel-label': { 
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  fontWeight: activeStep === index ? 600 : 400
                }
              }}
            >
              {step.label}
            </StepLabel>
            {isMobile && (
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {step.description}
                </Typography>
              </StepContent>
            )}
          </Step>
        ))}
      </Stepper>

      {/* Step Content */}
      <Box sx={{ minHeight: 400 }}>
        {/* Step 1: Customer Details */}
        {activeStep === 0 && (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Receipt /> Customer Information
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={formData.customerName}
                    onChange={(e) => handleInputChange('customerName', e.target.value)}
                    error={!!errors.customerName}
                    helperText={errors.customerName}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={formData.customerPhone}
                    onChange={(e) => handleInputChange('customerPhone', e.target.value)}
                    error={!!errors.customerPhone}
                    helperText={errors.customerPhone}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                    error={!!errors.customerEmail}
                    helperText={errors.customerEmail}
                    required
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Delivery/Takeaway Details */}
        {activeStep === 1 && (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {orderType === 'delivery' ? <LocalShipping /> : <ShoppingBag />}
                {orderType === 'delivery' ? 'Delivery Information' : 'Pickup Information'}
              </Typography>
              
              {orderType === 'delivery' ? (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Delivery Address"
                      multiline
                      rows={3}
                      value={formData.deliveryAddress}
                      onChange={(e) => handleInputChange('deliveryAddress', e.target.value)}
                      error={!!errors.deliveryAddress}
                      helperText={errors.deliveryAddress}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Delivery Instructions (Optional)"
                      multiline
                      rows={2}
                      value={formData.deliveryInstructions}
                      onChange={(e) => handleInputChange('deliveryInstructions', e.target.value)}
                      placeholder="e.g., Call when arriving, Leave at door, etc."
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={!!errors.pickupTime}>
                      <InputLabel>Pickup Time</InputLabel>
                      <Select
                        value={formData.pickupTime}
                        onChange={(e) => handleInputChange('pickupTime', e.target.value)}
                        label="Pickup Time"
                        required
                      >
                        {getPickupTimeSlots().map((time) => (
                          <MenuItem key={time} value={time}>
                            {time}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.pickupTime && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                          {errors.pickupTime}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Pickup Instructions (Optional)"
                      multiline
                      rows={2}
                      value={formData.pickupInstructions}
                      onChange={(e) => handleInputChange('pickupInstructions', e.target.value)}
                      placeholder="e.g., Call when ready, Special packaging, etc."
                    />
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Order Summary */}
        {activeStep === 2 && (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Payment /> Order Summary
              </Typography>
              
              {/* Order Items */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Order Items:</Typography>
                {cart.map((item, index) => (
                  <Box key={index} sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 1,
                    borderBottom: index < cart.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider'
                  }}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {item.name} x {item.quantity}
                      </Typography>
                      {item.selectedSize && (
                        <Typography variant="caption" color="text.secondary">
                          Size: {item.selectedSize.name}
                        </Typography>
                      )}
                    </Box>
                                         <Typography variant="body2" fontWeight={600}>
                       ${item.totalPrice || item.price}
                     </Typography>
                  </Box>
                ))}
              </Box>

              {/* Special Instructions */}
              <TextField
                fullWidth
                label="Special Instructions (Optional)"
                multiline
                rows={2}
                value={formData.specialInstructions}
                onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                placeholder="Any special requests for your order..."
                sx={{ mb: 3 }}
              />

              {/* Payment Method */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={formData.paymentMethod}
                  onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                  label="Payment Method"
                >
                  <MenuItem value="hitpay">💳 HitPay (Credit/Debit Card)</MenuItem>
                  <MenuItem value="cod">💰 Cash on Delivery</MenuItem>
                </Select>
              </FormControl>

              {/* Order Total */}
              <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                                 <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                   <Typography variant="body2">Subtotal:</Typography>
                   <Typography variant="body2">${cartTotal}</Typography>
                 </Box>
                 {deliveryFee > 0 && (
                   <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                     <Typography variant="body2">Delivery Fee:</Typography>
                     <Typography variant="body2">${deliveryFee}</Typography>
                   </Box>
                 )}
                 <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                   <Typography variant="body2">Taxes (18%):</Typography>
                   <Typography variant="body2">${taxes}</Typography>
                 </Box>
                 <Divider sx={{ my: 1 }} />
                 <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                   <Typography variant="h6" fontWeight="bold">Total:</Typography>
                   <Typography variant="h6" fontWeight="bold" color="primary">
                     ${finalTotal}
                   </Typography>
                 </Box>
              </Paper>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        mt: 4,
        flexDirection: isMobile ? 'column' : 'row',
        gap: 2
      }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          variant="outlined"
          sx={{ 
            minWidth: isMobile ? '100%' : 120,
            order: isMobile ? 2 : 1
          }}
        >
          Back
        </Button>
        
        <Box sx={{ display: 'flex', gap: 2, order: isMobile ? 1 : 2 }}>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Payment />}
              sx={{ 
                minWidth: isMobile ? '100%' : 150,
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              {loading ? 'Processing...' : 'Proceed to Payment'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{ 
                minWidth: isMobile ? '100%' : 120,
                py: 1.5
              }}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>

      {/* Progress Indicator */}
      {loading && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          bgcolor: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">Processing Payment...</Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we process your order
            </Typography>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default PaymentForm;
